import { useApolloClient } from '@apollo/client';
import React, { useState } from 'react';

import { useApiClient } from '@eluve/api-client-provider';
import { AudioPlayer } from '@eluve/audio-player';
import { SimpleTooltip } from '@eluve/components';
import {
  Button,
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Icon,
  LoadingIndicator,
  Separator,
  Stack,
  useToast,
} from '@eluve/components';
import {
  AppointmentFileTypesEnum,
  DbEnums,
  UserUploadStatusEnum,
} from '@eluve/graphql-types';
import { useDialog } from '@eluve/utility-hooks';
import { getDatadogUrlForFile } from '@eluve/utils';

import { appointmentFilesQuery } from './operations';

export interface AppointmentFileActionsColumnProps {
  tenantId: string;
  appointmentId: string;
  fileId: string;
  fileName?: string;
  filePath: string;
  fileType: AppointmentFileTypesEnum;
  fileStatus?: UserUploadStatusEnum;
  fileUploadedAt?: string;
  transcriptionStatus?: string;
}

const NonAudioFileTypes: AppointmentFileTypesEnum[] = [
  'RAW_TRANSCRIPT_METADATA',
  'CHART_PDF',
  'SUPERBILL_PDF',
];

export const AppointmentFileActionsColumn: React.FC<
  AppointmentFileActionsColumnProps
> = ({
  appointmentId,
  fileId,
  fileName,
  filePath,
  tenantId,
  fileType,
  fileStatus,
  fileUploadedAt,
  transcriptionStatus,
}) => {
  const api = useApiClient();
  const apolloClient = useApolloClient();
  const toast = useToast();

  const audioDialog = useDialog();
  const markCanceledDialog = useDialog();
  const rescheduleDialog = useDialog();

  const [audioUrl, setAudioUrl] = useState<string>('');
  const [markCanceledProcessing, setMarkCanceledProcessing] = useState(false);
  const [rescheduleProcessing, setRescheduleProcessing] = useState(false);

  const getFile = async (type: 'download' | 'listen') => {
    if (!filePath) return;

    if (type === 'listen') {
      audioDialog.openDialog();

      if (audioUrl) {
        return;
      }
    }

    const result = await api.adminAppointment.getAppointmentFileSignedUrl({
      params: { tenantId, appointmentId, fileId },
    });

    if (result.status === 200 && result.body.presignedUrl) {
      if (type === 'listen') {
        setAudioUrl(result.body.presignedUrl);
      } else {
        const fetched = await fetch(result.body.presignedUrl);
        const blob = await fetched.blob();
        const url = window.URL.createObjectURL(blob);

        const link = document.createElement('a');
        link.href = url;
        link.download = filePath.split('/').pop() ?? '';
        link.click();

        window.URL.revokeObjectURL(url);
      }
    } else {
      if (type === 'listen') {
        audioDialog.closeDialog();
      }

      toast.error('Failed to generate file URL');
    }
  };

  const isAudioFile = !NonAudioFileTypes.includes(fileType);
  const isCancelableAudio =
    fileType === 'USER_BACKUP_AUDIO' ||
    fileType === 'CHUNKED_AUDIO' ||
    fileType === 'WEBSOCKET_AUDIO';
  const isReschedulableAudio =
    fileType === 'USER_BACKUP_AUDIO' || fileType === 'CHUNKED_AUDIO';
  const isFileAvailable = Boolean(fileUploadedAt) && Boolean(filePath);

  const canMarkAsCanceled =
    fileStatus === 'PENDING_UPLOAD' ||
    fileStatus === 'PRE_PROVISIONED' ||
    fileStatus === 'UPLOAD_ERROR' ||
    transcriptionStatus === DbEnums.TranscriptStatus.FAILED;

  const canRescheduleTranscription =
    transcriptionStatus === DbEnums.TranscriptStatus.FAILED;

  const onMarkCanceled = async () => {
    setMarkCanceledProcessing(true);

    const result =
      await api.adminAppointment.overrideAudioFileAndTranscriptAsCancelled({
        params: { tenantId, appointmentId, fileId },
      });

    if (result.status !== 200) {
      toast.error('Failed to mark file and/or transcript as canceled');
    } else {
      apolloClient.refetchQueries({
        include: [appointmentFilesQuery],
      });
    }

    setMarkCanceledProcessing(false);
    markCanceledDialog.closeDialog();
  };

  const onRescheduleTranscription = async () => {
    setRescheduleProcessing(true);

    const result = await api.adminAppointment.rescheduleTranscription({
      params: { tenantId, appointmentId, fileId },
    });

    if (result.status !== 200) {
      toast.error('Failed to reschedule transcription');
    } else {
      apolloClient.refetchQueries({
        include: [appointmentFilesQuery],
      });
    }

    setRescheduleProcessing(false);
    rescheduleDialog.closeDialog();
  };

  if (!filePath) {
    return null;
  }

  return (
    <>
      <DropdownMenu>
        <SimpleTooltip label="More Actions">
          <DropdownMenuTrigger asChild>
            <Button variant="outline">
              <Icon name="Ellipsis" size="auto" />
            </Button>
          </DropdownMenuTrigger>
        </SimpleTooltip>
        <DropdownMenuContent align="start">
          {fileName && (
            <DropdownMenuItem
              onSelect={() =>
                window.open(getDatadogUrlForFile(fileName), '_blank')
              }
            >
              View Datadog Logs
            </DropdownMenuItem>
          )}
          <DropdownMenuItem
            disabled={!isFileAvailable}
            onSelect={() => getFile('download')}
          >
            Download File
          </DropdownMenuItem>
          {isAudioFile && (
            <DropdownMenuItem
              disabled={!isFileAvailable}
              onSelect={() => getFile('listen')}
            >
              Listen to Audio
            </DropdownMenuItem>
          )}
          {isCancelableAudio && (
            <DropdownMenuItem
              disabled={!canMarkAsCanceled}
              onSelect={() => markCanceledDialog.openDialog()}
            >
              Mark as Canceled
            </DropdownMenuItem>
          )}
          {isReschedulableAudio && (
            <DropdownMenuItem
              disabled={!canRescheduleTranscription}
              onSelect={() => rescheduleDialog.openDialog()}
            >
              Reschedule Transcription
            </DropdownMenuItem>
          )}
        </DropdownMenuContent>
      </DropdownMenu>

      <Dialog
        open={audioDialog.isDialogOpen}
        onOpenChange={audioDialog.setIsDialogOpen}
      >
        <DialogContent size="lg">
          <DialogHeader>
            <DialogTitle>Listen to Audio</DialogTitle>
          </DialogHeader>
          <Separator className="my-2" />
          <Stack
            align="center"
            className="relative min-h-16 py-4"
            justify="center"
            gap={2}
          >
            {audioUrl ? (
              <AudioPlayer audioUrl={audioUrl} />
            ) : (
              <LoadingIndicator size="xl" />
            )}
          </Stack>
        </DialogContent>
      </Dialog>

      <Dialog
        open={markCanceledDialog.isDialogOpen}
        onOpenChange={markCanceledDialog.setIsDialogOpen}
      >
        <DialogContent
          size="lg"
          showCloseButton={false}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This will mark the audio file and associated transcript as
              canceled, even though it currently appears to be missing.
              <br />
              <br />
              Note: a tag will also be added, indicating human intervention.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={markCanceledProcessing}
              onClick={markCanceledDialog.closeDialog}
            >
              Cancel
            </Button>
            <Button disabled={markCanceledProcessing} onClick={onMarkCanceled}>
              {markCanceledProcessing ? 'Processing...' : 'Mark as Canceled'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <Dialog
        open={rescheduleDialog.isDialogOpen}
        onOpenChange={rescheduleDialog.setIsDialogOpen}
      >
        <DialogContent
          size="lg"
          showCloseButton={false}
          onInteractOutside={(e) => e.preventDefault()}
        >
          <DialogHeader>
            <DialogTitle>Are you sure?</DialogTitle>
            <DialogDescription>
              This will schedule a new transcription attempt.
              <br />
              <br />
              Note: a tag will also be added, indicating human intervention.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button
              variant="outline"
              disabled={rescheduleProcessing}
              onClick={rescheduleDialog.closeDialog}
            >
              Cancel
            </Button>
            <Button
              disabled={rescheduleProcessing}
              onClick={onRescheduleTranscription}
            >
              {rescheduleProcessing ? 'Processing...' : 'Reschedule'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};
